/* eslint-disable i18next/no-literal-string */
import type {AttributeEntityType} from '@/types/attribute-entity-type'
import {authBase, iamBase, oauthBase} from '@/lib/runtime-config'
type Id = string | number

function identityRelationshipsByEndpoint(field: 'fromIdentityId' | 'toIdentityId', id: Id, type?: string): string {
    const base = `/identity-relationships?${field}=${id}`
    return iamBase(type !== undefined && type !== null && type !== '' ? `${base}&relationshipType=${type}` : base)
}

export const HttpMethod = {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    PATCH: 'PATCH',
    DELETE: 'DELETE',
} as const

export const endpoints = {
    auth: {
        impersonateStart: () => authBase('/impersonate/start'),
        impersonateStop: () => authBase('/impersonate/stop'),
        me: () => authBase('/me'),
        logout: () => authBase('/logout'),
        keycloakLogin: () => oauthBase('/oauth2/authorization/keycloak'),
    },
    config: () => iamBase('/config'),
    utilities: {
        inferPattern: () => iamBase('/utilities/infer-pattern'),
    },
    clock: {
        status: () => iamBase('/clock'),
    },
    users: {
        list: () => iamBase('/users'),
        links: () => iamBase('/users/links'),
    },
    identitiesLinks: () => iamBase('/identities/links'),
    systemsLinks: () => iamBase('/target-systems/links'),
    resourcesLinks: () => iamBase('/resources/links'),
    enforcementObjectsLinks: () => iamBase('/enforcement-objects/links'),
    accessProfilesLinks: () => iamBase('/access-profiles/links'),
    reportCategoriesLinks: () => iamBase('/reporting/categories/links'),
    /** Federated picker dispatcher. Optional `?type=` narrows; absent → searches all entity types caller has authority for (polymorphic-FK case for ErrorLog.sourceId). */
    links: () => iamBase('/links'),
    roles: {
        list: () => iamBase('/roles'),
        detail: (id: Id) => iamBase(`/roles/${id}`),
        permissions: () => iamBase('/roles/permissions'),
        create: () => iamBase('/roles'),
        update: (id: Id) => iamBase(`/roles/${id}`),
        remove: (id: Id) => iamBase(`/roles/${id}`),
        updatePermissions: (id: Id) => iamBase(`/roles/${id}/permissions`),
    },
    permissions: {
        list: () => iamBase('/permissions'),
        detail: (id: Id) => iamBase(`/permissions/${id}`),
    },
    parties: {
        list: () => iamBase('/parties'),
        detail: (id: Id) => iamBase(`/parties/${id}`),
        attributes: (id: Id) => iamBase(`/parties/${id}/attributes`),
        deleteAttribute: (id: Id, attributeId: Id) => iamBase(`/parties/${id}/attributes/${attributeId}`),
    },
    partyRelationships: {
        fromParty: (partyId: Id) => iamBase(`/party-relationships?fromPartyId=${partyId}`),
        toParty: (partyId: Id) => iamBase(`/party-relationships?toPartyId=${partyId}`),
        create: () => iamBase('/party-relationships'),
        end: (id: Id) => iamBase(`/party-relationships/${id}/end`),
    },
    identityRelationships: {
        fromIdentity: (identityId: Id, type?: string) =>
            identityRelationshipsByEndpoint('fromIdentityId', identityId, type),
        toIdentity: (identityId: Id, type?: string) =>
            identityRelationshipsByEndpoint('toIdentityId', identityId, type),
        create: () => iamBase('/identity-relationships'),
        end: (id: Id) => iamBase(`/identity-relationships/${id}/end`),
    },
    identities: {
        list: () => iamBase('/identities'),
        detail: (id: Id) => iamBase(`/identities/${id}`),
        byParty: (partyId: Id) => iamBase(`/identities/by-party/${partyId}`),
        attributes: (id: Id) => iamBase(`/identities/${id}/attributes`),
        identifiers: (id: Id) => iamBase(`/identities/${id}/identifiers`),
        deleteAttribute: (id: Id, attributeId: Id) => iamBase(`/identities/${id}/attributes/${attributeId}`),
        assignments: (id: Id) => iamBase(`/identities/${id}/assignments`),
        sources: (id: Id) => iamBase(`/identities/${id}/sources`),
        inheritedAccessProfileAssignments: (id: Id) => iamBase(`/identities/${id}/inherited-access-profile-assignments`),
        accounts: (id: Id) => iamBase(`/identities/${id}/accounts`),
        timeline: (id: Id) => iamBase(`/identities/${id}/timeline`),
        grants: (id: Id) => iamBase(`/identities/${id}/grants`),
        grantStats: (id: Id) => iamBase(`/identities/${id}/grants/stats`),
        accessSnapshot: (id: Id) => iamBase(`/identities/${id}/access-snapshot`),
        identitySnapshot: (id: Id) => iamBase(`/identities/${id}/identity-snapshot`),
        compareGrants: (idA: Id, idB: Id) => iamBase(`/identities/compare-grants?identityIdA=${idA}&identityIdB=${idB}`),
        updateLifecycle: (id: Id) => iamBase(`/identities/${id}/lifecycle`),
        updateEntryDate: (id: Id) => iamBase(`/identities/${id}/entry-date`),
        updateExitDate: (id: Id) => iamBase(`/identities/${id}/exit-date`),
        updateReactivationDate: (id: Id) => iamBase(`/identities/${id}/reactivation-date`),
        upcomingEvents: () => iamBase('/identities/lifecycle/upcoming'),
        lifecycleEvents: (id: Id) => iamBase(`/identities/${id}/lifecycle-events`),
        revokeAssignment: (id: Id, assignmentId: Id) => iamBase(`/identities/${id}/assignments/${assignmentId}/revoke`),
        recalculateGrants: (id: Id) => iamBase(`/identities/${id}/grants/recalculate`),
        evaluateRules: (id: Id) => iamBase(`/identities/${id}/evaluate-rules`),
        rehire: (id: Id) => iamBase(`/identities/${id}/rehire`),
        // WHY: placeholder for error-reporter sample test data — no live
        // backend endpoint, but the registry is the only place a raw
        // iamBase(`/...`) literal may live (issue 497).
        bulk: () => iamBase('/identities/bulk'),
    },
    constraints: {
        list: () => iamBase('/constraints'),
        detail: (id: Id) => iamBase(`/constraints/${id}`),
        enable: (id: Id) => iamBase(`/constraints/${id}/enable`),
        disable: (id: Id) => iamBase(`/constraints/${id}/disable`),
        review: (id: Id) => iamBase(`/constraints/${id}/review`),
        trigger: (id: Id) => iamBase(`/constraints/${id}/trigger`),
    },
    violations: {
        list: () => iamBase('/violations'),
        detail: (id: Id) => iamBase(`/violations/${id}`),
        summaryByEntity: () => iamBase('/violations/summary'),
        ack: (id: Id) => iamBase(`/violations/${id}/ack`),
        bulkAck: () => iamBase('/violations/bulk-ack'),
    },
    accounts: {
        list: () => iamBase('/accounts'),
        detail: (id: Id) => iamBase(`/accounts/${id}`),
        createAccount: (identityId: Id, targetSystemId: Id) => iamBase(`/identities/${identityId}/systems/${targetSystemId}/create-account`),
        disableOnSystem: (accountId: Id) => iamBase(`/accounts/${accountId}/disable-on-system`),
        enableOnSystem: (accountId: Id) => iamBase(`/accounts/${accountId}/enable-on-system`),
        deleteOnSystem: (accountId: Id) => iamBase(`/accounts/${accountId}/delete-on-system`),
        disable: (accountId: Id) => iamBase(`/accounts/${accountId}/disable`),
        enable: (accountId: Id) => iamBase(`/accounts/${accountId}/enable`),
        lock: (accountId: Id) => iamBase(`/accounts/${accountId}/lock`),
        delete: (accountId: Id) => iamBase(`/accounts/${accountId}/delete`),
        statusEvents: (accountId: Id) => iamBase(`/accounts/${accountId}/status-events`),
    },
    systems: {
        list: () => iamBase('/target-systems'),
        detail: (id: Id) => iamBase(`/target-systems/${id}`),
        grants: (id: Id) => iamBase(`/target-systems/${id}/grants`),
        attributes: (id: Id) => iamBase(`/target-systems/${id}/attributes`),
        deleteAttribute: (id: Id, attributeId: Id) => iamBase(`/target-systems/${id}/attributes/${attributeId}`),
        owners: (id: Id) => iamBase(`/target-systems/${id}/owners`),
        removeOwner: (id: Id, ownerId: Id) => iamBase(`/target-systems/${id}/owners/${ownerId}`),
    },
    resources: {
        list: () => iamBase('/resources'),
        detail: (id: Id) => iamBase(`/resources/${id}`),
        attributes: (id: Id) => iamBase(`/resources/${id}/attributes`),
        deleteAttribute: (id: Id, attributeId: Id) => iamBase(`/resources/${id}/attributes/${attributeId}`),
        owners: (id: Id) => iamBase(`/resources/${id}/owners`),
        removeOwner: (id: Id, ownerId: Id) => iamBase(`/resources/${id}/owners/${ownerId}`),
        bySystem: (systemId: Id) => iamBase(`/resources/by-system/${systemId}`),
        children: (id: Id) => iamBase(`/resources/${id}/children`),
        ancestors: (id: Id) => iamBase(`/resources/${id}/ancestors`),
        tree: (id: Id) => iamBase(`/resources/${id}/tree`),
        roots: () => iamBase('/resources/roots'),
        setParent: (id: Id) => iamBase(`/resources/${id}/parent`),
        removeParent: (id: Id) => iamBase(`/resources/${id}/parent`),
    },
    enforcementObjects: {
        list: () => iamBase('/enforcement-objects'),
        detail: (id: Id) => iamBase(`/enforcement-objects/${id}`),
        byResource: (resourceId: Id) => iamBase(`/enforcement-objects/by-resource/${resourceId}`),
        bySystem: (systemId: Id) => iamBase(`/enforcement-objects/by-system/${systemId}`),
    },
    outboundConnectors: {
        list: () => iamBase('/outbound-connectors'),
        detail: (id: Id) => iamBase(`/outbound-connectors/${id}`),
        logs: (id: Id) => iamBase(`/outbound-connectors/${id}/logs`),
        bySystem: (systemId: Id) => iamBase(`/outbound-connectors/by-system/${systemId}`),
        create: () => iamBase('/outbound-connectors'),
        update: (id: Id) => iamBase(`/outbound-connectors/${id}`),
        remove: (id: Id) => iamBase(`/outbound-connectors/${id}`),
        scimConfig: (id: Id) => iamBase(`/outbound-connectors/${id}/scim-config`),
        ldapConfig: (id: Id) => iamBase(`/outbound-connectors/${id}/ldap-config`),
        restConfig: (id: Id) => iamBase(`/outbound-connectors/${id}/rest-config`),
        databaseConfig: (id: Id) => iamBase(`/outbound-connectors/${id}/database-config`),
        k8sConfig: (id: Id) => iamBase(`/outbound-connectors/${id}/k8s-config`),
        emailConfig: (id: Id) => iamBase(`/outbound-connectors/${id}/email-config`),
        test: (id: Id) => iamBase(`/outbound-connectors/${id}/test`),
        updateAttributeMappings: (id: Id) => iamBase(`/outbound-connectors/${id}/attribute-mappings`),
        // CPD-OFF — mirrors inbound scriptConfig endpoints (URL shape shared by design)
        scriptConfig: (id: Id) => iamBase(`/outbound-connectors/${id}/script-config`),
        scriptConfigValidate: (id: Id) => iamBase(`/outbound-connectors/${id}/script-config/validate`),
        // CPD-ON
        sapAbapConfig: (id: Id) => iamBase(`/outbound-connectors/${id}/sap-abap-config`),
        sapAbapAuthorizations: (id: Id) => iamBase(`/sap-abap/connectors/${id}/authorizations`),
        provisioningTasks: (id: Id) => iamBase(`/outbound-connectors/${id}/provisioning-tasks`),
        provisioningHistory: (id: Id) => iamBase(`/outbound-connectors/${id}/provisioning-history`),
    },
    accessProfiles: {
        list: () => iamBase('/access-profiles'),
        detail: (id: Id) => iamBase(`/access-profiles/${id}`),
        owners: (id: Id) => iamBase(`/access-profiles/${id}/owners`),
        removeOwner: (id: Id, ownerId: Id) => iamBase(`/access-profiles/${id}/owners/${ownerId}`),
        bindings: (id: Id) => iamBase(`/access-profiles/${id}/bindings`),
        removeBinding: (bindingId: Id) => iamBase(`/access-profiles/bindings/${bindingId}`),
        create: () => iamBase('/access-profiles'),
        update: (id: Id) => iamBase(`/access-profiles/${id}`),
        remove: (id: Id) => iamBase(`/access-profiles/${id}`),
        addBinding: (id: Id) => iamBase(`/access-profiles/${id}/bindings`),
        activate: (id: Id) => iamBase(`/access-profiles/${id}/activate`),
        deprecate: (id: Id) => iamBase(`/access-profiles/${id}/deprecate`),
        byResourceInherited: (resourceId: Id) => iamBase(`/access-profiles/by-resource/${resourceId}/inherited`),
        byEnforcementObject: (eoId: Id) => iamBase(`/access-profiles/by-enforcement-object/${eoId}`),
        functionGroups: (id: Id) => iamBase(`/access-profiles/${id}/function-groups`),
    },
    approvalPolicies: {
        list: () => iamBase('/approval-policies'),
        detail: (id: Id) => iamBase(`/approval-policies/${id}`),
        create: () => iamBase('/approval-policies'),
        update: (id: Id) => iamBase(`/approval-policies/${id}`),
        remove: (id: Id) => iamBase(`/approval-policies/${id}`),
    },
    escalationThresholds: {
        list: () => iamBase('/escalation-thresholds'),
        update: (id: Id) => iamBase(`/escalation-thresholds/${id}`),
    },
    escalationEvents: {
        list: (stepId: Id, stepType: string) =>
            iamBase(`/escalation-events?stepId=${stepId}&stepType=${encodeURIComponent(stepType)}`),
    },
    serviceAccounts: {
        list: () => iamBase('/auth/service-accounts'),
        detail: (id: Id) => iamBase(`/auth/service-accounts/${id}`),
        create: () => iamBase('/auth/service-accounts'),
        update: (id: Id) => iamBase(`/auth/service-accounts/${id}`),
        remove: (id: Id) => iamBase(`/auth/service-accounts/${id}`),
    },
    apiKeys: {
        list: (serviceAccountId: Id) => iamBase(`/auth/service-accounts/${serviceAccountId}/api-keys`),
        mint: (serviceAccountId: Id) => iamBase(`/auth/service-accounts/${serviceAccountId}/api-keys`),
        revoke: (serviceAccountId: Id, keyId: Id) =>
            iamBase(`/auth/service-accounts/${serviceAccountId}/api-keys/${keyId}/revoke`),
    },
    serviceAccountRoles: {
        list: (serviceAccountId: Id) => iamBase(`/auth/service-accounts/${serviceAccountId}/roles`),
        assign: (serviceAccountId: Id) => iamBase(`/auth/service-accounts/${serviceAccountId}/roles`),
        remove: (serviceAccountId: Id, roleId: Id) =>
            iamBase(`/auth/service-accounts/${serviceAccountId}/roles/${roleId}`),
    },
    certificationPolicies: {
        list: () => iamBase('/certification-policies'),
        detail: (id: Id) => iamBase(`/certification-policies/${id}`),
        create: () => iamBase('/certification-policies'),
        update: (id: Id) => iamBase(`/certification-policies/${id}`),
        remove: (id: Id) => iamBase(`/certification-policies/${id}`),
        campaignSummary: (id: Id) => iamBase(`/certification-policies/${id}/campaign-summary`),
        testExpression: () => iamBase('/certification-policies/test-expression'),
        previewScope: () => iamBase('/certification-policies/preview-scope'),
        owners: (id: Id) => iamBase(`/certification-policies/${id}/owners`),
        removeOwner: (id: Id, ownerId: Id) => iamBase(`/certification-policies/${id}/owners/${ownerId}`),
    },
    certificationCampaigns: {
        list: () => iamBase('/certification-campaigns'),
        detail: (id: Id) => iamBase(`/certification-campaigns/${id}`),
        create: () => iamBase('/certification-campaigns'),
        update: (id: Id) => iamBase(`/certification-campaigns/${id}`),
        remove: (id: Id) => iamBase(`/certification-campaigns/${id}`),
        activate: (id: Id) => iamBase(`/certification-campaigns/${id}/activate`),
        cancel: (id: Id) => iamBase(`/certification-campaigns/${id}/cancel`),
        generate: () => iamBase('/certification-campaigns/generate'),
        statistics: (id: Id) => iamBase(`/certification-campaigns/${id}/statistics`),
        previewStatistics: (id: Id) => iamBase(`/certification-campaigns/${id}/preview-statistics`),
        reviewItems: (id: Id) => iamBase(`/certification-campaigns/${id}/items`),
        owners: (id: Id) => iamBase(`/certification-campaigns/${id}/owners`),
        removeOwner: (id: Id, ownerId: Id) => iamBase(`/certification-campaigns/${id}/owners/${ownerId}`),
    },
    certificationReviews: {
        myCampaigns: () => iamBase('/certification-reviews/my/campaigns'),
        myReviews: () => iamBase('/certification-reviews/my'),
        detail: (reviewItemId: Id) => iamBase(`/certification-reviews/${reviewItemId}`),
        decisions: (reviewItemId: Id) => iamBase(`/certification-reviews/${reviewItemId}/decisions`),
        decide: (reviewItemId: Id) => iamBase(`/certification-reviews/${reviewItemId}/decide`),
        bulkDecide: (reviewItemId: Id) => iamBase(`/certification-reviews/${reviewItemId}/bulk-decide`),
        certify: (reviewItemId: Id) => iamBase(`/certification-reviews/${reviewItemId}/certify`),
        reassign: (reviewItemId: Id) => iamBase(`/certification-reviews/${reviewItemId}/reassign`),
        navigation: (reviewItemId: Id) => iamBase(`/certification-reviews/${reviewItemId}/navigation`),
        bulkCertify: () => iamBase('/certification-reviews/bulk-certify'),
    },
    certificationDashboard: {
        summary: () => iamBase('/certification-dashboard/summary'),
        activeCampaigns: () => iamBase('/certification-dashboard/active-campaigns'),
    },
    myAccess: {
        assignments: () => iamBase('/my/access'),
        timeline: () => iamBase('/my/timeline'),
        functionGroupAssignments: () => iamBase('/my/function-group-access'),
        functionGroupTimeline: () => iamBase('/my/function-group-access/timeline'),
        effectiveAccess: () => iamBase('/my/effective-access'),
    },
    delegations: {
        myIncoming: () => iamBase('/my/delegations'),
        myOutgoing: () => iamBase('/my/delegations/outgoing'),
        mySearch: () => iamBase('/my/delegations/search'),
        myCreate: () => iamBase('/my/delegations'),
        myUpdate: (id: Id) => iamBase(`/my/delegations/${id}`),
        myRevoke: (id: Id) => iamBase(`/my/delegations/${id}/revoke`),
        adminList: () => iamBase('/delegations'),
        adminRevoke: (id: Id) => iamBase(`/delegations/${id}/revoke`),
        forIdentity: (identityId: Id) => iamBase(`/identities/${identityId}/delegations`),
    },
    accessRequests: {
        list: () => iamBase('/access-requests'),
        detail: (id: Id) => iamBase(`/access-requests/${id}`),
        myRequests: () => iamBase('/access-requests/my-requests'),
        myRequestDetail: (id: Id) => iamBase(`/access-requests/my-requests/${id}`),
        myApprovals: () => iamBase('/access-requests/my-approvals'),
        myApprovalsCount: () => iamBase('/access-requests/my-approvals/count'),
        peerRecommendations: (beneficiaryId: Id) => iamBase(`/access-requests/peer-recommendations/${beneficiaryId}`),
        profileHoldings: () => iamBase('/access-requests/profile-holdings'),
        create: () => iamBase('/access-requests'),
        createBulk: () => iamBase('/access-requests/bulk'),
        cancel: (id: Id) => iamBase(`/access-requests/${id}/cancel`),
        approveItem: (itemId: Id) => iamBase(`/access-requests/items/${itemId}/approve`),
        rejectItem: (itemId: Id) => iamBase(`/access-requests/items/${itemId}/reject`),
        cancelItem: (itemId: Id) => iamBase(`/access-requests/items/${itemId}/cancel`),
        batchApprove: () => iamBase('/access-requests/items/batch-approve'),
        batchReject: () => iamBase('/access-requests/items/batch-reject'),
    },
    autoAssignRules: {
        list: () => iamBase('/auto-assign-rules'),
        detail: (id: Id) => iamBase(`/auto-assign-rules/${id}`),
        versions: (id: Id) => iamBase(`/auto-assign-rules/${id}/versions`),
        profiles: (id: Id) => iamBase(`/auto-assign-rules/${id}/profiles`),
        executions: (id: Id) => iamBase(`/auto-assign-rules/${id}/executions`),
        allExecutions: () => iamBase('/auto-assign-rules/executions'),
        test: (ruleId: Id) => iamBase(`/auto-assign-rules/${ruleId}/test`),
        requests: () => iamBase('/auto-assign-rules/requests'),
        requestDetail: (requestId: Id) => iamBase(`/auto-assign-rules/requests/${requestId}`),
        validateExpression: () => iamBase('/auto-assign-rules/requests/validate-expression'),
        createRequest: () => iamBase('/auto-assign-rules/requests'),
        updateRequest: (requestId: Id) => iamBase(`/auto-assign-rules/requests/${requestId}`),
        removeRequest: (requestId: Id) => iamBase(`/auto-assign-rules/requests/${requestId}`),
        submitRequest: (requestId: Id) => iamBase(`/auto-assign-rules/requests/${requestId}/submit`),
        approveRequest: (requestId: Id) => iamBase(`/auto-assign-rules/requests/${requestId}/approve`),
        rejectRequest: (requestId: Id) => iamBase(`/auto-assign-rules/requests/${requestId}/reject`),
        cancelRequest: (requestId: Id) => iamBase(`/auto-assign-rules/requests/${requestId}/cancel`),
        testExpression: () => iamBase('/auto-assign-rules/requests/test-expression'),
        testExistingRequest: (requestId: Id) => iamBase(`/auto-assign-rules/requests/${requestId}/test`),
        myApprovals: () => iamBase('/auto-assign-rules/requests/my-approvals'),
        myApprovalsCount: () => iamBase('/auto-assign-rules/requests/my-approvals/count'),
        evaluate: (id: Id) => iamBase(`/auto-assign-rules/${id}/evaluate`),
        impactAnalysis: (id: Id) => iamBase(`/auto-assign-rules/${id}/impact-analysis`),
        owners: (id: Id) => iamBase(`/auto-assign-rules/${id}/owners`),
        removeOwner: (id: Id, ownerId: Id) => iamBase(`/auto-assign-rules/${id}/owners/${ownerId}`),
    },
    restrictionRuleRequests: {
        list: () => iamBase('/restriction-rule-requests'),
        detail: (id: Id) => iamBase(`/restriction-rule-requests/${id}`),
        create: () => iamBase('/restriction-rule-requests'),
        update: (id: Id) => iamBase(`/restriction-rule-requests/${id}`),
        remove: (id: Id) => iamBase(`/restriction-rule-requests/${id}`),
        submit: (id: Id) => iamBase(`/restriction-rule-requests/${id}/submit`),
        approve: (id: Id) => iamBase(`/restriction-rule-requests/${id}/approve`),
        reject: (id: Id) => iamBase(`/restriction-rule-requests/${id}/reject`),
        cancel: (id: Id) => iamBase(`/restriction-rule-requests/${id}/cancel`),
        myApprovals: () => iamBase('/restriction-rule-requests/my-approvals'),
        myApprovalsCount: () => iamBase('/restriction-rule-requests/my-approvals/count'),
        test: (id: Id) => iamBase(`/restriction-rule-requests/${id}/test`),
        validateExpression: () => iamBase('/restriction-rule-requests/validate-expression'),
    },
    types: {
        list: () => iamBase('/types'),
        locales: () => iamBase('/types/locales'),
        promoted: () => iamBase('/types/promoted'),
        detail: (tableName: string) => iamBase(`/types/${tableName}`),
        create: (tableName: string) => iamBase(`/types/${tableName}`),
        remove: (tableName: string, code: string) => iamBase(`/types/${tableName}/${code}`),
        updateTranslations: (tableName: string, code: string) => iamBase(`/types/${tableName}/${code}/translations`),
        toggleActive: (tableName: string, code: string) => iamBase(`/types/${tableName}/${code}/active`),
        updateSettings: (tableName: string, code: string) => iamBase(`/types/${tableName}/${code}/settings`),
    },
    effectiveAccess: {
        stats: () => iamBase('/effective-access/stats'),
        calculationStatus: (identityId: Id) => iamBase(`/effective-access/status/${identityId}`),
    },
    provisioningTasks: {
        list: () => iamBase('/provisioning-tasks'),
        detail: (id: Id) => iamBase(`/provisioning-tasks/${id}`),
        stats: () => iamBase('/provisioning-tasks/stats'),
        history: () => iamBase('/provisioning-tasks/history'),
        historyByIdentity: (identityId: Id) => iamBase(`/provisioning-tasks/history/by-identity/${identityId}`),
        processAll: () => iamBase('/provisioning-tasks/process-all'),
        requeueReady: () => iamBase('/provisioning-tasks/requeue-ready'),
        archive: () => iamBase('/provisioning-tasks/archive'),
        retry: (id: Id) => iamBase(`/provisioning-tasks/${id}/retry`),
        retryFailed: () => iamBase('/provisioning-tasks/retry-failed'),
    },
    nodes: {
        list: () => iamBase('/nodes'),
        current: () => iamBase('/nodes/current'),
        remove: (nodeId: Id) => iamBase(`/nodes/${nodeId}`),
    },
    jobs: {
        list: () => iamBase('/jobs'),
        detail: (id: Id) => iamBase(`/jobs/${id}`),
        update: (id: Id) => iamBase(`/jobs/${id}`),
        remove: (id: Id) => iamBase(`/jobs/${id}`),
        create: () => iamBase('/jobs'),
        stats: () => iamBase('/jobs/stats'),
        byKey: (jobKey: string) => iamBase(`/jobs/key/${jobKey}`),
        trigger: (jobId: Id) => iamBase(`/jobs/${jobId}/trigger`),
        jobExecutions: (id: Id) => iamBase(`/jobs/${id}/executions`),
        tasks: (id: Id) => iamBase(`/jobs/${id}/tasks`),
        updateTasks: (jobId: Id) => iamBase(`/jobs/${jobId}/tasks`),
        taskPalette: (jobId: Id) => iamBase(`/jobs/${jobId}/tasks/palette`),
        preview: (jobId: Id) => iamBase(`/jobs/${jobId}/preview`),
        builder: (id: Id) => iamBase(`/jobs/${id}/builder`),
        updateTaskLogLevel: (taskId: Id) => iamBase(`/jobs/tasks/${taskId}/log-level`),
        validateScript: () => iamBase('/jobs/validate-script'),
        scriptAssist: () => iamBase('/jobs/script-assist'),
        schedule: (jobId: Id) => iamBase(`/jobs/${jobId}/schedule`),
    },
    executions: {
        list: () => iamBase('/jobs/executions'),
        detail: (id: Id) => iamBase(`/jobs/executions/${id}`),
        tasks: (id: Id) => iamBase(`/jobs/executions/${id}/tasks`),
        cancel: (execId: Id) => iamBase(`/jobs/executions/${execId}/cancel`),
        // SSE streams — issue 497
        stream: () => iamBase('/jobs/executions/stream'),
        streamForExecution: (id: Id) => iamBase(`/jobs/executions/${id}/stream`),
        streamForJob: (jobId: Id) => iamBase(`/jobs/executions/job/${jobId}/stream`),
    },
    executionLogs: {
        byReference: (referenceId: Id) => iamBase(`/execution-logs/reference/${referenceId}`),
    },
    reporting: {
        categories: () => iamBase('/reporting/categories'),
        categoryDetail: (id: Id) => iamBase(`/reporting/categories/${id}`),
        transferCategory: (id: Id) => iamBase(`/reporting/categories/${id}/transfer`),
        moveCategory: (id: Id) => iamBase(`/reporting/categories/${id}/move`),
        categoryReports: (categoryId: Id) => iamBase(`/reporting/categories/${categoryId}/reports`),
        reportDetail: (reportId: Id) => iamBase(`/reporting/reports/${reportId}`),
        moveReport: (reportId: Id) => iamBase(`/reporting/reports/${reportId}/move`),
        executeReport: (reportId: Id) => iamBase(`/reporting/reports/${reportId}/execute`),
        reportExecutions: (reportId: Id) => iamBase(`/reporting/reports/${reportId}/executions`),
        executionDetail: (executionId: Id) => iamBase(`/reporting/executions/${executionId}`),
        deleteReport: (reportId: Id) => iamBase(`/reporting/reports/${reportId}`),
        searchReports: () => iamBase('/reporting/reports/search'),
        bulkDeleteReports: () => iamBase('/reporting/reports/bulk-delete'),
        bulkMoveReports: () => iamBase('/reporting/reports/bulk-move'),
        exportReport: (reportId: Id) => iamBase(`/reporting/reports/${reportId}/export`),
        reorderCategories: () => iamBase('/reporting/categories/reorder'),
        reorderReports: () => iamBase('/reporting/reports/reorder'),
        sqlAssist: () => iamBase('/reporting/sql/assist'),
        sqlExecute: () => iamBase('/reporting/sql/execute'),
        sqlModels: () => iamBase('/reporting/sql/models'),
        sqlSchema: () => iamBase('/reporting/sql/schema'),
        // CPD-OFF
        reportAttributeDefinitions: (reportId: Id) => iamBase(`/reporting/reports/${reportId}/attribute-definitions`),
        reportAttributeDefinition: (reportId: Id, defId: Id) => iamBase(`/reporting/reports/${reportId}/attribute-definitions/${defId}`),
        // CPD-ON
    },
    scripts: {
        sqlPreview: () => iamBase('/scripts/sql/preview'),
    },
    ruleExpressions: {
        suggestions: () => iamBase('/rule-expressions/suggestions'),
    },
    restrictionRules: {
        list: () => iamBase('/restriction-rules'),
        detail: (id: Id) => iamBase(`/restriction-rules/${id}`),
        create: () => iamBase('/restriction-rules'),
        update: (id: Id) => iamBase(`/restriction-rules/${id}`),
        activate: (id: Id) => iamBase(`/restriction-rules/${id}/activate`),
        deactivate: (id: Id) => iamBase(`/restriction-rules/${id}/deactivate`),
        profiles: (id: Id) => iamBase(`/restriction-rules/${id}/profiles`),
        test: () => iamBase('/restriction-rules/test'),
        testSavedRule: (id: Id) => iamBase(`/restriction-rules/${id}/test`),
        violations: () => iamBase('/restriction-rules/violations'),
        ruleViolations: (id: Id) => iamBase(`/restriction-rules/${id}/violations`),
        detect: (id: Id) => iamBase(`/restriction-rules/${id}/detect`),
        allDetections: () => iamBase('/restriction-rules/detections'),
        detections: (id: Id) => iamBase(`/restriction-rules/${id}/detections`),
        versions: (id: Id) => iamBase(`/restriction-rules/${id}/versions`),
        owners: (id: Id) => iamBase(`/restriction-rules/${id}/owners`),
        removeOwner: (id: Id, ownerId: Id) => iamBase(`/restriction-rules/${id}/owners/${ownerId}`),
    },
    attributeDefinitions: (() => {
        const byId = (entityType: AttributeEntityType, id: Id) => iamBase(`/${entityType}/attribute-definitions/${id}`)
        return {
            list: (entityType: AttributeEntityType) => iamBase(`/${entityType}/attribute-definitions`),
            detail: byId,
            create: (entityType: AttributeEntityType) => iamBase(`/${entityType}/attribute-definitions`),
            update: byId,
            remove: byId,
            translations: (entityType: AttributeEntityType, id: Id) => iamBase(`/${entityType}/attribute-definitions/${id}/translations`),
            resolvedValues: (entityType: AttributeEntityType, id: Id) => iamBase(`/${entityType}/attribute-definitions/${id}/resolved-values`),
            resolveValuesDraft: () => iamBase('/attribute-definitions/resolve-values'),
            validate: (entityType: AttributeEntityType) => iamBase(`/${entityType}/attribute-definitions/validate`),
            allowedValueSources: () => iamBase('/allowed-value-sources'),
            reorder: (entityType: AttributeEntityType) => iamBase(`/${entityType}/attribute-definitions/reorder`),
            bulkSet: (entityType: AttributeEntityType) => iamBase(`/${entityType}/attributes/bulk`),
            bulkImport: (entityType: AttributeEntityType) => iamBase(`/${entityType}/attributes/bulk-import`),
        }
    })(),
    // CPD-OFF — FG endpoints mirror AP endpoint patterns by design
    functionGroups: {
        list: () => iamBase('/function-groups'),
        detail: (id: Id) => iamBase(`/function-groups/${id}`),
        attributes: (id: Id) => iamBase(`/function-groups/${id}/attributes`),
        deleteAttribute: (id: Id, attributeId: Id) => iamBase(`/function-groups/${id}/attributes/${attributeId}`),
        accessProfiles: (id: Id) => iamBase(`/function-groups/${id}/access-profiles`),
        removeAccessProfile: (id: Id, linkId: Id) => iamBase(`/function-groups/${id}/access-profiles/${linkId}`),
        owners: (id: Id) => iamBase(`/function-groups/${id}/owners`),
        removeOwner: (id: Id, ownerId: Id) => iamBase(`/function-groups/${id}/owners/${ownerId}`),
    },
    functionGroupAssignments: {
        byIdentity: (identityId: Id) => iamBase(`/identities/${identityId}/function-group-assignments`),
        byFunctionGroup: (functionGroupId: Id) => iamBase(`/function-groups/${functionGroupId}/assignments`),
        revoke: (identityId: Id, assignmentId: Id) => iamBase(`/identities/${identityId}/function-group-assignments/${assignmentId}/revoke`),
        restore: (identityId: Id, assignmentId: Id) => iamBase(`/identities/${identityId}/function-group-assignments/${assignmentId}/restore`),
        extend: (identityId: Id, assignmentId: Id) => iamBase(`/identities/${identityId}/function-group-assignments/${assignmentId}/extend`),
    },
    functionGroupRequests: {
        list: () => iamBase('/function-group-requests'),
        detail: (id: Id) => iamBase(`/function-group-requests/${id}`),
        create: () => iamBase('/function-group-requests'),
        cancel: (id: Id) => iamBase(`/function-group-requests/${id}/cancel`),
        approveItem: (id: Id, itemId: Id) => iamBase(`/function-group-requests/${id}/items/${itemId}/approve`),
        rejectItem: (id: Id, itemId: Id) => iamBase(`/function-group-requests/${id}/items/${itemId}/reject`),
        cancelItem: (id: Id, itemId: Id) => iamBase(`/function-group-requests/${id}/items/${itemId}/cancel`),
        batchApprove: () => iamBase('/function-group-requests/items/batch-approve'),
        batchReject: () => iamBase('/function-group-requests/items/batch-reject'),
        myRequests: () => iamBase('/function-group-requests/my-requests'),
        myRequestDetail: (id: Id) => iamBase(`/function-group-requests/my-requests/${id}`),
        myApprovals: () => iamBase('/function-group-requests/my-approvals'),
        myApprovalsCount: () => iamBase('/function-group-requests/my-approvals/count'),
        holdings: () => iamBase('/function-group-requests/holdings'),
    },
    partyRelationshipSchema: {
        list: () => iamBase('/party-relationship-schema'),
        detail: (id: Id) => iamBase(`/party-relationship-schema/${id}`),
        create: () => iamBase('/party-relationship-schema'),
        update: (id: Id) => iamBase(`/party-relationship-schema/${id}`),
        deactivate: (id: Id) => iamBase(`/party-relationship-schema/${id}`),
        violations: () => iamBase('/party-relationship-schema/violations'),
        enforcementMode: () => iamBase('/party-relationship-schema/enforcement-mode'),
    },
    errorLogs: {
        list: () => iamBase('/error-logs'),
        detail: (id: Id) => iamBase(`/error-logs/${id}`),
        errorTypes: () => iamBase('/error-logs/error-types'),
        errorTypeOccurrences: (templateId: Id) => iamBase(`/error-logs/error-types/${templateId}/occurrences`),
        stats: () => iamBase('/error-logs/stats'),
        badgeCount: () => iamBase('/error-logs/badge-count'),
        bySource: () => iamBase('/error-logs/by-source'),
        contextKeys: () => iamBase('/error-logs/context-keys'),
        generateTestData: () => iamBase('/error-logs/generate-test-data'),
        deleteAll: () => iamBase('/error-logs'),
    },
    scriptExecuteLog: {
        list: () => iamBase('/admin/script-execute-log'),
    },
    admin: {
        frontendError: () => iamBase('/admin/frontend-error'),
        generateTestData: () => iamBase('/admin/generate-testdata'),
        resetConnectorCache: () => iamBase('/admin/reset-connector-cache'),
    },
    lifecycleEvents: {
        list: () => iamBase('/lifecycle-events'),
    },
    entityAttributes: {
        list: (entityType: AttributeEntityType, entityId: Id) => iamBase(`/${entityType}/${entityId}/attributes`),
        create: (entityType: AttributeEntityType, entityId: Id) => iamBase(`/${entityType}/${entityId}/attributes`),
        update: (entityType: AttributeEntityType, entityId: Id) => iamBase(`/${entityType}/${entityId}/attributes`),
        remove: (entityType: AttributeEntityType, entityId: Id, attributeId: Id) => iamBase(`/${entityType}/${entityId}/attributes/${attributeId}`),
    },
    caches: {
        list: () => iamBase('/admin/caches'),
        evict: (name: string) => iamBase(`/admin/caches/${encodeURIComponent(name)}/evict`),
        reload: (name: string) => iamBase(`/admin/caches/${encodeURIComponent(name)}/reload`),
    },
    llmPrompts: {
        list: () => iamBase('/admin/llm-prompts'),
        detail: (key: string) => iamBase(`/admin/llm-prompts/${encodeURIComponent(key)}`),
        update: (key: string) => iamBase(`/admin/llm-prompts/${encodeURIComponent(key)}`),
        restoreDefault: (key: string) => iamBase(`/admin/llm-prompts/${encodeURIComponent(key)}/restore-default`),
        renderPreview: (key: string) => iamBase(`/admin/llm-prompts/${encodeURIComponent(key)}/render-preview`),
        test: (key: string) => iamBase(`/admin/llm-prompts/${encodeURIComponent(key)}/test`),
    },
    notificationTemplates: {
        list: () => iamBase('/notifications/notification-templates'),
        detail: (key: Id) => iamBase(`/notifications/notification-templates/${encodeURIComponent(key)}`),
        update: (key: Id) => iamBase(`/notifications/notification-templates/${encodeURIComponent(key)}`),
        restoreDefault: (key: Id) => iamBase(`/notifications/notification-templates/${encodeURIComponent(key)}/restore-default`),
        testSend: (key: Id) => iamBase(`/notifications/notification-templates/${encodeURIComponent(key)}/test-send`),
    },
    notifications: {
        list: () => iamBase('/notifications'),
        detail: (id: Id) => iamBase(`/notifications/${id}`),
        unreadCount: () => iamBase('/notifications/unread-count'),
        markRead: (id: Id) => iamBase(`/notifications/${id}/read`),
        markAllRead: () => iamBase('/notifications/read-all'),
        log: () => iamBase('/notifications/log'),
    },
    formLayouts: {
        list: () => iamBase('/form-layouts'),
        detail: (useCaseKey: Id) => iamBase(`/form-layouts/${useCaseKey}`),
        full: (useCaseKey: Id) => iamBase(`/form-layouts/${useCaseKey}/full`),
        upsert: (useCaseKey: Id) => iamBase(`/form-layouts/${useCaseKey}`),
        delete: (useCaseKey: Id) => iamBase(`/form-layouts/${useCaseKey}`),
        palette: (useCaseKey: Id) => iamBase(`/form-layouts/palette/${useCaseKey}`),
    },
    onboarding: {
        submit: () => iamBase('/onboarding'),
        checkPartyName: (displayName: string) => iamBase(`/onboarding/check-party-name?displayName=${encodeURIComponent(displayName)}`),
        checkIdentifier: (type: string, value: string) => iamBase(`/onboarding/check-identifier?type=${encodeURIComponent(type)}&value=${encodeURIComponent(value)}`),
    },
    inboundConnectors: {
        list: () => iamBase('/inbound-connectors'),
        detail: (id: Id) => iamBase(`/inbound-connectors/${id}`),
        create: () => iamBase('/inbound-connectors'),
        update: (id: Id) => iamBase(`/inbound-connectors/${id}`),
        remove: (id: Id) => iamBase(`/inbound-connectors/${id}`),
        enable: (id: Id) => iamBase(`/inbound-connectors/${id}/enable`),
        disable: (id: Id) => iamBase(`/inbound-connectors/${id}/disable`),
        test: (id: Id) => iamBase(`/inbound-connectors/${id}/test`),
        restConfig: (id: Id) => iamBase(`/inbound-connectors/${id}/config/rest`),
        updateRestConfig: (id: Id) => iamBase(`/inbound-connectors/${id}/config/rest`),
        scimConfig: (id: Id) => iamBase(`/inbound-connectors/${id}/config/scim`),
        updateScimConfig: (id: Id) => iamBase(`/inbound-connectors/${id}/config/scim`),
        updateSettings: (id: Id) => iamBase(`/inbound-connectors/${id}/settings`),
        syncFull: (id: Id) => iamBase(`/inbound-connectors/${id}/sync/full`),
        syncDelta: (id: Id) => iamBase(`/inbound-connectors/${id}/sync/delta`),
        syncPreview: (id: Id) => iamBase(`/inbound-connectors/${id}/sync/preview`),
        syncStatus: (id: Id) => iamBase(`/inbound-connectors/${id}/sync/status`),
        syncRuns: (id: Id) => iamBase(`/inbound-connectors/${id}/sync/runs`),
        syncRunDetail: (id: Id, runId: Id) => iamBase(`/inbound-connectors/${id}/sync/runs/${runId}`),
        syncRecords: (id: Id, runId: Id) => iamBase(`/inbound-connectors/${id}/sync/runs/${runId}/records`),
        approveSyncRun: (id: Id, runId: Id) => iamBase(`/inbound-connectors/${id}/sync/runs/${runId}/approve`),
        rejectSyncRun: (id: Id, runId: Id) => iamBase(`/inbound-connectors/${id}/sync/runs/${runId}/reject`),
        attributeMappings: (id: Id) => iamBase(`/inbound-connectors/${id}/attribute-mappings`),
        inboundScriptConfig: (id: Id) => iamBase(`/inbound-connectors/${id}/script-config`),
        inboundScriptConfigValidate: (id: Id) => iamBase(`/inbound-connectors/${id}/script-config/validate`),
        inboundScriptConfigTestConnection: (id: Id) => iamBase(`/inbound-connectors/${id}/script-config/test-connection`),
        sapHcmConfig: (id: Id) => iamBase(`/inbound-connectors/${id}/sap-hcm-config`),
        sapSfEcConfig: (id: Id) => iamBase(`/inbound-connectors/${id}/sap-sf-ec-config`),
    },
    valueLists: {
        list: () => iamBase('/value-lists'),
        detail: (id: Id) => iamBase(`/value-lists/${id}`),
        create: () => iamBase('/value-lists'),
        update: (id: Id) => iamBase(`/value-lists/${id}`),
        remove: (id: Id) => iamBase(`/value-lists/${id}`),
        addEntry: (id: Id) => iamBase(`/value-lists/${id}/entries`),
        updateEntry: (id: Id, entryId: Id) => iamBase(`/value-lists/${id}/entries/${entryId}`),
        removeEntry: (id: Id, entryId: Id) => iamBase(`/value-lists/${id}/entries/${entryId}`),
        reorderEntries: (id: Id) => iamBase(`/value-lists/${id}/entries/reorder`),
    },
    observability: {
        traces: () => iamBase('/observability/traces'),
        trace: (sessionId: Id) => iamBase(`/observability/traces/${sessionId}`),
        span: (spanId: Id) => iamBase(`/observability/spans/${spanId}`),
        sessions: () => iamBase('/observability/sessions'),
        health: () => iamBase('/observability/health'),
        frontendSpans: () => iamBase('/observability/frontend-spans'),
    },
    rehireReview: {
        list: () => iamBase('/rehire-review'),
        count: () => iamBase('/rehire-review/count'),
        detail: (id: Id) => iamBase(`/rehire-review/${id}`),
        decide: (id: Id) => iamBase(`/rehire-review/${id}/decide`),
        mine: {
            list: () => iamBase('/rehire-review/mine'),
            count: () => iamBase('/rehire-review/mine/count'),
            detail: (id: Id) => iamBase(`/rehire-review/mine/${id}`),
            previousAccess: (id: Id) => iamBase(`/rehire-review/mine/${id}/previous-access`),
        },
    },
} as const
